import { useEffect, useRef } from "react";

const PURPLE = [184, 174, 255];
const LIME = [186, 205, 28];

const terrainMask = (x, y, w, h) => {
  const a = Math.max(0, 1 - Math.hypot((x - w * 0.06) / (w * 0.5), (y - h * 0.95) / (h * 0.95)));
  const b = Math.max(0, 1 - Math.hypot((x - w * 0.96) / (w * 0.46), (y - h * 0.05) / (h * 0.9)));
  return Math.min(1, a * 1.25 + b * 1.25);
};

export const HeroBackground = ({ variant = "both", className = "", intensity = 1 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let w = 0, h = 0, raf = 0, last = 0, visible = true, stars = [], pulses = [], nextPulse = 0;
    const mouse = { x: -999, y: -999 };
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const resize = () => {
      const r = cv.getBoundingClientRect();
      w = r.width; h = r.height;
      cv.width = w * dpr; cv.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      stars = Array.from({ length: Math.round((w * h) / 11000) }, () => ({ x: Math.random() * w, y: Math.random() * h, p: Math.random() * 6.28, s: 0.4 + Math.random() * 1.1 }));
    };
    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      const s = t / 1000;
      if (variant !== "stars") {
        const gap = 9;
        for (let y = gap / 2; y < h; y += gap) {
          for (let x = gap / 2; x < w; x += gap) {
            const m = terrainMask(x, y, w, h);
            if (m < 0.02) continue;
            const wave = 0.5 + 0.5 * Math.sin(x * 0.012 + s * 0.6) * Math.cos(y * 0.018 - s * 0.45) + 0.25 * Math.sin((x + y) * 0.006 + s * 0.3);
            const near = Math.max(0, 1 - Math.hypot(x - mouse.x, y - mouse.y) / 140);
            const v = Math.min(1, m * (0.35 + 0.65 * wave) + near * 0.5);
            if (v < 0.06) continue;
            const peak = v > 0.93;
            const c = peak ? LIME : PURPLE;
            ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${((peak ? 0.7 : 0.22 + v * 0.7) * (peak ? Math.min(1, intensity * 1.3) : intensity)).toFixed(3)})`;
            ctx.beginPath();
            ctx.arc(x, y, 0.5 + v * 1.6, 0, 6.283);
            ctx.fill();
          }
        }
      }
      if (variant !== "terrain") {
        for (const st of stars) {
          const a = (0.25 + 0.7 * (0.5 + 0.5 * Math.sin(s * 1.6 + st.p))) * Math.max(intensity, 0.7);
          ctx.fillStyle = `rgba(236,235,228,${a.toFixed(3)})`;
          ctx.fillRect(st.x, st.y, st.s + 0.4, st.s + 0.4);
        }
        if (t > nextPulse) {
          pulses.push({ x: w * (0.15 + Math.random() * 0.7), y: h * (0.1 + Math.random() * 0.7), t0: t, big: Math.random() > 0.6 });
          nextPulse = t + 1400 + Math.random() * 1800;
        }
        pulses = pulses.filter((p) => t - p.t0 < 1800);
        for (const p of pulses) {
          const k = (t - p.t0) / 1800;
          const c = p.big ? LIME : PURPLE;
          ctx.strokeStyle = `rgba(${c[0]},${c[1]},${c[2]},${(0.6 * (1 - k)).toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 2 + k * (p.big ? 46 : 24), 0, 6.283);
          ctx.stroke();
          ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${(0.9 * (1 - k)).toFixed(3)})`;
          ctx.fillRect(p.x - 1, p.y - 1, 2, 2);
        }
      }
    };
    const loop = (t) => {
      raf = requestAnimationFrame(loop);
      if (!visible || t - last < 33) return;
      last = t;
      draw(t);
    };
    resize();
    if (reduce) draw(0); else raf = requestAnimationFrame(loop);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(cv);
    const onMove = (e) => { const r = cv.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; };
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => { cancelAnimationFrame(raf); io.disconnect(); window.removeEventListener("resize", resize); window.removeEventListener("mousemove", onMove); };
  }, [variant, intensity]);
  return <canvas ref={ref} aria-hidden data-testid={`hero-bg-${variant}`} className={`pointer-events-none absolute inset-0 h-full w-full ${className}`} />;
};
